import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Partner.css';

const PARTNER_ROLES = [
  'Reseller / Channel Partner',
  'Technology / ISV Partner',
  'MSSP / MDR Provider',
  'System Integrator / Consulting',
  'Distributor',
  'Other',
];

const sanitize = (str) => str.replace(/[<>"'&]/g, '');

const PartnerApply = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselectedType = params.get('type') || '';
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    partnerType: PARTNER_ROLES.includes(preselectedType) ? preselectedType : '',
    otherPartnerType: '',
    website: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const renderRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.enterprise && recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
        window.grecaptcha.enterprise.render(recaptchaRef.current, {
          sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
          action: 'partner_apply',
          callback: (token) => {
            setRecaptchaToken(token);
            setError('');
          },
          'expired-callback': () => {
            setRecaptchaToken(null);
          },
        });
      }
    };

    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready(renderRecaptcha);
    } else {
      const interval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval);
          window.grecaptcha.enterprise.ready(renderRecaptcha);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: sanitize(value) });
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName || !form.lastName || !form.email || !form.company || !form.partnerType) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge.');
      return;
    }

    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'https://bltzbackend.vercel.app';
      const response = await fetch(`${apiBase}/api/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recaptchaToken,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          company: form.company,
          partnerType: form.partnerType === 'Other' ? form.otherPartnerType : form.partnerType,
          website: form.website || null,
          message: form.message || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Partner submission error:', err.message);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [form, recaptchaToken]);

  return (
    <>
      <section className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">Become a Partner</h1>
      </section>

      <section className="ct-content ptn-apply-content">
        <div className="ct-layout">
          <div className="ct-left">
            <h2 className="ct-heading">Partner Application</h2>
            <p className="ct-description">
              Join the Bltz AI partner ecosystem. Fill out the form and our partnerships team will
              reach out within one business day to discuss next steps.
            </p>

            <div className="ct-info-cards">
              <div className="ct-info-card">
                <h4>What to Expect</h4>
                <p>After submitting, a Bltz partnerships manager will reach out to learn about your business, review partner tiers, and outline mutual go-to-market opportunities.</p>
              </div>
              <div className="ct-info-card">
                <h4>Partner Benefits</h4>
                <p>Competitive margins, deal protection, dedicated support, co-marketing, and access to our partner portal with training and collateral.</p>
              </div>
            </div>
          </div>

          <div className="ct-right">
            {submitted ? (
              <div className="ct-success">
                <h3>Application Received!</h3>
                <p>Thank you for your interest in partnering with Bltz AI. Our partnerships team will be in touch within one business day.</p>
                <button className="ct-explore-btn" onClick={() => navigate('/partner')}>Back to Partner Overview &rarr;</button>
              </div>
            ) : (
              <form className="ct-form" onSubmit={handleSubmit}>
                <div className="ct-form-row">
                  <div className="ct-field">
                    <label>First name*</label>
                    <input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="ct-field">
                    <label>Last name*</label>
                    <input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="ct-field">
                  <label>Business email*</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    maxLength={200}
                    autoComplete="email"
                  />
                </div>

                <div className="ct-field">
                  <label>Company*</label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    required
                    maxLength={200}
                    autoComplete="organization"
                  />
                </div>

                <div className="ct-field">
                  <label>Partner type*</label>
                  <select name="partnerType" value={form.partnerType} onChange={handleChange} required>
                    <option value="">Select partner type</option>
                    {PARTNER_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {form.partnerType === 'Other' && (
                  <div className="ct-field">
                    <label>Please specify</label>
                    <input
                      type="text"
                      name="otherPartnerType"
                      value={form.otherPartnerType}
                      onChange={handleChange}
                      maxLength={100}
                      placeholder="Enter partner type"
                    />
                  </div>
                )}

                <div className="ct-field">
                  <label>Company website</label>
                  <input
                    type="url"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    maxLength={300}
                    placeholder="https://"
                    autoComplete="url"
                  />
                </div>

                <div className="ct-field">
                  <label>Tell us about your business</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={2000}
                    placeholder="How would you like to partner with Bltz AI?"
                  />
                </div>

                <div ref={recaptchaRef} className="ct-recaptcha"></div>

                {error && <p className="ct-error">{error}</p>}

                <button
                  type="submit"
                  className="ct-submit-btn"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>

                <p className="ct-privacy">
                  By submitting, you agree to our <span onClick={() => { navigate('/privacy'); window.scrollTo(0, 0); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default PartnerApply;
